import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import config from '../config';
import { toast } from 'sonner';
import { usePricing } from '../hooks/usePricing';
import { useSavedProListings } from '../hooks/useSavedProListings';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import ShowContactButton from '../components/ShowContactButton';
import ReportListingButton from '../components/ReportListingButton';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MapPin, ShieldCheck, Star, Heart, Clock, Briefcase, FileCheck, Share2, Facebook, MessageSquare, Phone, MessageCircle, Link2 } from 'lucide-react';

/** Renders the 1–5 star row for a single review. */
const StarRow = ({ rating, t }) => (
  <div
    className="flex items-center gap-0.5"
    role="img"
    aria-label={t('services.ratingOutOf', { rating })}
  >
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        size={14}
        className={s <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
        aria-hidden="true"
      />
    ))}
  </div>
);

const rateLabel = (listing, t) => {
  const { rate_type, rate_min, rate_max } = listing;
  if (rate_type === 'negotiable' || (rate_min == null && rate_max == null)) {
    return t('services.negotiable');
  }
  const suffix =
    rate_type === 'hourly' ? t('services.perHour')
    : rate_type === 'per_project' ? ` ${t('services.perProject')}`
    : ` ${t('services.perService')}`;
  if (rate_min != null && rate_max != null && rate_min !== rate_max) {
    return `₱${Number(rate_min).toLocaleString()}–₱${Number(rate_max).toLocaleString()}${suffix}`;
  }
  const amount = rate_min != null ? rate_min : rate_max;
  return `${t('services.from')} ₱${Number(amount).toLocaleString()}${suffix}`;
};

/**
 * Public detail page for a Pro listing at /services/listings/:slug. Contact info is NEVER in the
 * by-slug payload — it comes only from ShowContactButton's contact call (§11). Flag-gated shell
 * when services_enabled is false.
 */
const ServiceListingDetail = ({ profile, setProfile }) => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { pricing, loading: pricingLoading } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);
  const { isSaved, toggleSave, loading: savingListing } = useSavedProListings(profile);

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  useEffect(() => {
    // While pricing is still resolving, keep the spinner up (do NOT flip loading off) so a
    // cold visit to a shared link never flashes the not-found screen before the fetch starts.
    if (!servicesEnabled) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);   // reset on slug change so a prior failure can't stick to a new fetch
      try {
        const res = await api.get(`/api/pro-listings/by-slug/${slug}`);
        if (cancelled) return;
        setListing(res.data?.listing || null);
        setActivePhoto(0);
      } catch (err) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, servicesEnabled]);

  // Reviews are keyed off the listing id (public, released/non-blind only).
  const listingId = listing?.id;
  useEffect(() => {
    if (!servicesEnabled || !listingId) { setReviews([]); return; }
    let cancelled = false;
    (async () => {
      setReviewsLoading(true);
      try {
        const res = await api.get(`/api/pro-listings/${listingId}/reviews`);
        if (cancelled) return;
        setReviews(Array.isArray(res.data?.reviews) ? res.data.reviews : []);
      } catch (err) {
        if (!cancelled) setReviews([]);
      } finally {
        if (!cancelled) setReviewsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listingId, servicesEnabled]);

  // --- Sharing ---------------------------------------------------------------
  // Mirrors GigDetails.js/JobDetails.js. Every channel links to the BACKEND share
  // endpoint, never the SPA route: /api/pro-listings/{slug}/share is what serves the
  // Open Graph tags (photo, title, rate) that Facebook scrapes for the preview card.
  // The SPA route would be scraped as the bare React shell, which has no meta tags.

  // Legacy clipboard path: in-app browsers and non-secure contexts have no
  // navigator.clipboard, so a promise-only copy silently does nothing there.
  const legacyCopy = (text, successMessage) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    let copied = false;
    try {
      // execCommand returns false on a denied copy rather than throwing, so the
      // return value has to be checked or a failed copy reports success.
      copied = document.execCommand('copy');
    } catch (err) {
      copied = false;
    }
    document.body.removeChild(textArea);
    if (copied) {
      toast.success(successMessage, { duration: 4000 });
    } else {
      toast.info(`${t('services.shareThisLink', 'Share this link:')} ${text}`, { duration: 8000 });
    }
  };

  const copyShareLink = (text) => {
    const successMessage = t('services.linkCopied', 'Link copied! Paste anywhere to share.');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success(successMessage, { duration: 4000 }))
        .catch(() => legacyCopy(text, successMessage));
    } else {
      legacyCopy(text, successMessage);
    }
  };

  const handleShare = (platform) => {
    if (!listing) return;

    const shareEndpoint = `${config.BACKEND_URL}/api/pro-listings/${listing.slug || slug}/share`;
    // rateLabel() is the same formatter the page renders, so the share text quotes the
    // price exactly as the visitor sees it (and is localized, unlike the OG card).
    const rateText = rateLabel(listing, t);
    const areaText = Array.isArray(listing.service_area) && listing.service_area.length
      ? listing.service_area[0]
      : t('services.boholLabel', 'Bohol');
    const shareText = `${listing.title} — ${areaText}${rateText ? ` — ${rateText}` : ''} | ${t('services.shareQuoteCta', 'Hire a trusted local pro on Bohol Trabaho!')} #BoholServices #scammerfree`;

    if (platform === 'facebook') {
      if (typeof window.FB !== 'undefined') {
        window.FB.ui({
          method: 'share',
          href: shareEndpoint,
          quote: shareText,
        }, function (response) {
          // Cancelling is not an error: the SDK reports it either as no response at
          // all or as error_code 4201 ("User canceled the Dialog flow"), so neither
          // shape may toast.
          const cancelled = !response || response.error_code === 4201;
          if (cancelled) return;
          if (response.error_message) {
            toast.error(`${t('services.shareFailed', 'Failed to share')}: ${response.error_message}`);
            // Put the link on the clipboard so a failed share isn't a dead end
            copyShareLink(shareEndpoint);
          } else {
            toast.success(t('services.sharedToFacebook', 'Shared to Facebook!'), { duration: 3000 });
          }
        });
        // Closed synchronously: if the SDK never invokes the callback (failed init,
        // popup killed), a callback-only close would strand the dialog open forever.
        setShowShareDialog(false);
        return;
      }

      // FB SDK not loaded — centred popup onto the share dialog directly.
      const fbShareUrl = `https://www.facebook.com/dialog/share?app_id=1387027799794224&display=popup&href=${encodeURIComponent(shareEndpoint)}&redirect_uri=${encodeURIComponent(shareEndpoint)}`;
      const width = 600;
      const height = 600;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      const popup = window.open(
        fbShareUrl,
        'facebook-share-dialog',
        `width=${width},height=${height},left=${left},top=${top},toolbar=0,status=0,scrollbars=1`
      );

      if (popup) {
        toast.success(t('services.openingFacebook', 'Opening Facebook share dialog...'), { duration: 3000 });
        setShowShareDialog(false);
      } else {
        // Popup blocked — fall back to the clipboard so the share isn't a dead end
        copyShareLink(shareEndpoint);
      }
      return;
    }

    if (platform === 'viber') {
      window.open(`viber://forward?text=${encodeURIComponent(`${shareText} ${shareEndpoint}`)}`, '_blank');
      setShowShareDialog(false);
      toast.success(t('services.openingViber', 'Opening Viber...'), { duration: 2000 });
      return;
    }

    if (platform === 'copy') {
      copyShareLink(shareEndpoint);
      setShowShareDialog(false);
      return;
    }

    const shareUrls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareEndpoint}`)}`,
      messenger: `fb-messenger://share/?link=${encodeURIComponent(shareEndpoint)}`,
    };
    window.open(shareUrls[platform], '_blank');
    setShowShareDialog(false);
    if (platform === 'messenger') {
      toast.success(t('services.openingMessenger', 'Opening Messenger...'), { duration: 2000 });
    }
  };

  if (!pricingLoading && !servicesEnabled) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-3">{t('services.comingSoonTitle')}</h1>
          <p className="text-muted-foreground">{t('services.comingSoonBody')}</p>
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  if (pricingLoading || loading) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="flex justify-center py-24">
          <div className="spinner" role="status" aria-label={t('common.loading', 'Loading...')} />
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  if (notFound || !listing) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="text-xl font-semibold mb-3">{t('services.detailNotFoundTitle')}</h1>
          <p className="text-muted-foreground mb-6">{t('services.detailNotFoundBody')}</p>
          <Button onClick={() => navigate('/services')}>{t('services.backToDirectory')}</Button>
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  const photos = Array.isArray(listing.photos) ? listing.photos.filter((p) => p && p.url) : [];
  const areas = Array.isArray(listing.service_area) ? listing.service_area : [];
  const saved = isSaved(listing.id);
  const hasRating = listing.rating != null && listing.review_count > 0;
  const confirmedReviews = reviews.filter((r) => r.transaction_confirmed);
  const unconfirmedReviews = reviews.filter((r) => !r.transaction_confirmed);

  const renderReview = (r, i) => (
    <div key={`${r.reviewer_name || 'anon'}-${r.created_at || i}`} className="py-3 border-b border-border last:border-0">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{r.reviewer_name || t('services.reviewAnonymous')}</span>
          {!r.transaction_confirmed && (
            <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-600 border-gray-200">
              {t('services.reviewUnconfirmedTag')}
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
        </span>
      </div>
      <StarRow rating={Number(r.rating) || 0} t={t} />
      {r.comment && (
        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line break-words">{r.comment}</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Photo gallery */}
          <div>
            {photos.length > 0 ? (
              <>
                <div className="rounded-lg overflow-hidden bg-muted aspect-[4/3]">
                  <img
                    src={photos[activePhoto]?.url}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                {photos.length > 1 && (
                  <div className="flex gap-2 mt-2 overflow-x-auto">
                    {photos.map((p, i) => (
                      <button
                        key={`${p.url}-${i}`}
                        type="button"
                        onClick={() => setActivePhoto(i)}
                        className={`shrink-0 h-16 w-16 rounded-md overflow-hidden border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${i === activePhoto ? 'border-primary' : 'border-transparent'}`}
                        aria-label={t('services.viewPhoto', { number: i + 1 })}
                        aria-current={i === activePhoto ? 'true' : undefined}
                      >
                        <img src={p.url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg bg-muted aspect-[4/3] flex items-center justify-center" aria-hidden="true">
                <Briefcase className="text-muted-foreground" size={40} />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold break-words">{listing.title}</h1>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {listing.id_verified && (
                  <Badge variant="verified">
                    <ShieldCheck size={12} className="mr-1" aria-hidden="true" />
                    {t('services.verified')}
                  </Badge>
                )}
              </div>
            </div>

            {listing.category?.name && (
              <p className="text-sm text-muted-foreground">{listing.category.name}</p>
            )}

            {hasRating && (
              <div className="flex items-center gap-1 text-sm">
                <Star size={16} className="fill-amber-400 text-amber-400" aria-hidden="true" />
                <span className="font-semibold">{listing.rating}</span>
                <span className="text-muted-foreground">
                  {t('services.reviewCount', { count: listing.review_count })}
                </span>
              </div>
            )}

            <p className="text-lg font-semibold text-primary">{rateLabel(listing, t)}</p>

            {listing.experience_years != null && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Briefcase size={15} aria-hidden="true" />
                {t('services.experienceYears', { count: listing.experience_years })}
              </div>
            )}

            {listing.availability_note && (
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <Clock size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>{listing.availability_note}</span>
              </div>
            )}

            {areas.length > 0 && (
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>{areas.join(', ')}</span>
              </div>
            )}

            {Array.isArray(listing.credential_types) && listing.credential_types.length > 0 && (
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileCheck size={15} className="text-emerald-600" aria-hidden="true" />
                  {t('services.credentialsOnFile')}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {listing.credential_types.map((c) => (
                    <Badge key={c} variant="outline" className="text-[11px]">
                      {t(`services.verifDocType_${c}`, c)}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{t('services.credentialsRevealHint')}</p>
              </div>
            )}

            <div className="pt-2 space-y-2">
              <ShowContactButton key={listing.id} listingId={listing.id} slug={listing.slug} profile={profile} />
              <Button
                variant="outline"
                className="w-full"
                disabled={savingListing}
                onClick={() => toggleSave(listing.id)}
              >
                <Heart
                  size={16}
                  className={`mr-2 ${saved ? 'fill-red-500 text-red-500' : ''}`}
                  aria-hidden="true"
                />
                {saved ? t('services.savedLabel') : t('services.saveLabel')}
              </Button>
              {/* Facebook-blue share CTA, matching the jobs/gigs pages. Ungated: anyone
                  looking at a public listing can share it, logged in or not. */}
              <Button
                variant="outline"
                className="w-full rounded-xl border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2]/10 transition-colors"
                onClick={() => setShowShareDialog(true)}
                data-testid="share-dropdown-btn"
              >
                <Share2 size={16} className="mr-2" aria-hidden="true" />
                {t('services.shareListing')}
              </Button>
            </div>
          </div>
        </div>

        {/* Description */}
        {listing.description && (
          <Card className="mt-6 p-4">
            <h2 className="font-heading text-xl font-semibold mb-3">{t('services.aboutThisService')}</h2>
            <p className="text-sm text-muted-foreground whitespace-pre-line break-words">
              {listing.description}
            </p>
          </Card>
        )}

        {/* Reviews */}
        <Card className="mt-6 p-4">
          <h2 className="font-heading text-xl font-semibold mb-3 flex items-center gap-2">
            <Star size={18} className="fill-amber-400 text-amber-400" aria-hidden="true" />
            {t('services.reviewsHeading')}
          </h2>
          {reviewsLoading ? (
            <p className="text-sm text-muted-foreground py-2">{t('services.reviewsLoading')}</p>
          ) : reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">{t('services.reviewsEmpty')}</p>
          ) : (
            <div>
              {confirmedReviews.map((r, i) => renderReview(r, i))}
              {unconfirmedReviews.length > 0 && (
                <>
                  <div className="flex items-center gap-2 pt-4 pb-1">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('services.reviewsUnconfirmedDivider')}
                    </span>
                    <span className="flex-1 border-t border-dashed border-border" aria-hidden="true" />
                  </div>
                  <p className="text-xs text-muted-foreground pb-1">{t('services.reviewsUnconfirmedHint')}</p>
                  {unconfirmedReviews.map((r, i) => renderReview(r, i))}
                </>
              )}
            </div>
          )}
        </Card>

        {/* Report — unobtrusive, near the bottom */}
        <div className="mt-6 flex justify-center">
          <ReportListingButton listingId={listing.id} />
        </div>
      </div>

      {/* Share dialog — same five channels as the gigs page */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('services.shareListing')}</DialogTitle>
            <DialogDescription>{t('services.chooseShareMethod')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('facebook')}
              data-testid="share-facebook-btn"
            >
              <Facebook size={18} className="mr-2 text-[#1877F2]" fill="#1877F2" />
              {t('services.shareFacebook')}
            </Button>
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('messenger')}
              data-testid="share-messenger-btn"
            >
              <MessageSquare size={18} className="mr-2 text-[#0099FF]" />
              {t('services.shareMessenger')}
            </Button>
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('viber')}
              data-testid="share-viber-btn"
            >
              <Phone size={18} className="mr-2 text-[#7360F2]" />
              {t('services.shareViber')}
            </Button>
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('whatsapp')}
              data-testid="share-whatsapp-btn"
            >
              <MessageCircle size={18} className="mr-2 text-[#25D366]" />
              {t('services.shareWhatsapp')}
            </Button>
            <Button
              className="w-full rounded-xl"
              variant="outline"
              onClick={() => handleShare('copy')}
              data-testid="copy-link-btn"
            >
              <Link2 size={18} className="mr-2 text-gray-600" />
              {t('services.copyLink')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BottomNav profile={profile} />
    </div>
  );
};

export default ServiceListingDetail;
